/**
 * API Client
 * Handles all API communication with backend
 */

import { fetchWithAuth } from '@/utils/fetchWithAuth';
import { getSecureToken } from '@/utils/tokenEncryption';
import { BILLING_API_URL } from '@/config/env';

// Use proxy in development, same origin in production
const API_URL = '';

export class ApiError extends Error {
  retryable: boolean;
  statusCode?: number;
  constructor(message: string, retryable = false, statusCode?: number) {
    super(message);
    this.name = 'ApiError';
    this.retryable = retryable;
    this.statusCode = statusCode;
  }
}

// Request cache and deduplication
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const requestCache = new Map<string, CacheEntry<any>>();
const inflightRequests = new Map<string, Promise<any>>();
const CACHE_TTL = 30000; // 30 seconds cache TTL
const endpointCooldownUntil = new Map<string, number>();

function isEndpointInCooldown(endpointKey: string): { active: boolean; retryAfterMs: number } {
  const until = endpointCooldownUntil.get(endpointKey) || 0;
  const now = Date.now();
  if (until > now) {
    return { active: true, retryAfterMs: until - now };
  }
  return { active: false, retryAfterMs: 0 };
}

function setEndpointCooldown(endpointKey: string, response: Response, fallbackMs = 10000): void {
  const retryAfterHeader = response.headers.get('Retry-After');
  const retryAfterMs = retryAfterHeader ? Math.max(0, parseInt(retryAfterHeader, 10) * 1000) : fallbackMs;
  endpointCooldownUntil.set(endpointKey, Date.now() + Math.max(retryAfterMs, 2000));
}

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  error?: string;
  retryAfter?: number;
  data?: T;
}

export interface Location {
  id: string;
  name: string;
  description?: string;
}

export interface Template {
  id: string;
  name: string;
  description?: string;
  basePrompt?: string;
  type?: string; // Type of template: 'photography', 'illustration', 'infographic', 'icon', 'mockup', 'digital-mockup', 'abstract', 'packshot', 'product', 'auto'
  workspace_id?: number | null;
  availability?: string;
  previewUrl?: string | null; // URL to template preview image (from Directus key_visual)
  thumbnail?: string | null; // UUID of thumbnail image from Directus
}

export interface PresetData {
  cameras: Record<string, any>;
  cameraSettings?: {
    shotPlans?: Record<string, any>;
    whiteBalance?: Record<string, any>;
    focusMode?: Record<string, any>;
    meteringMode?: Record<string, any>;
    aperture?: Record<string, any>;
    shutterSpeed?: Record<string, any>;
    iso?: Record<string, any>;
  };
}

export interface GenerateImageResponse {
  success: boolean;
  image: string;
  mimeType: string;
  filename: string;
  prompt: string;
  presets?: Record<string, any> | null;
  finalPrompt?: string;
  model?: string;
  id?: string | null; // UUID slug from Directus for rating
  designSessionId?: string | null;
  iterationIndex?: number | null;
  directus?: {
    fileId?: string;
    recordId?: string;
    slug?: string | null;
    url?: string;
    designSessionId?: string | null;
    iterationIndex?: number | null;
  };
  credits?: number; // Updated credits after generation (for authenticated users)
}

interface BillingTopupCheckoutResponse {
  success: boolean;
  checkout_url?: string;
  session_id?: string;
  error?: string;
  message?: string;
}

interface BillingPlan {
  price_id: string;
  plan_code?: string | null;
  display_name?: string | null;
  currency?: string | null;
  unit_amount?: number | null;
  price_formatted?: string | null;
  recurring_interval?: string | null;
  recurring_interval_count?: number | null;
  monthly_credits?: number | null;
}

interface BillingTopupPack {
  price_id: string;
  credits: number;
  currency?: string | null;
  unit_amount?: number | null;
  price_formatted?: string | null;
}

interface BillingCatalogResponse {
  success: boolean;
  plans?: BillingPlan[];
  topups?: BillingTopupPack[];
}

export interface ImageInfo {
  id?: string; // UUID slug from Directus
  url: string;
  filename: string;
  timestamp: number;
  params?: Record<string, any>;
}

export interface DesignSessionState {
  geminiHistory: any[];
  thoughtSignature: string | null;
  lastParams: Record<string, any> | null;
}

/**
 * Helper to get cached data or execute request with deduplication
 */
async function getCachedOrFetch<T>(
  cacheKey: string,
  fetcher: () => Promise<T>,
  ttl: number = CACHE_TTL
): Promise<T> {
  // Check cache first
  const cached = requestCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < ttl) {
    return cached.data;
  }

  // Check if request is already in-flight
  if (inflightRequests.has(cacheKey)) {
    return inflightRequests.get(cacheKey)!;
  }


  // Execute request and cache
  const promise = fetcher()
    .then((data) => {
      requestCache.set(cacheKey, { data, timestamp: Date.now() });
      inflightRequests.delete(cacheKey);
      return data;
    })
    .catch((error) => {
      inflightRequests.delete(cacheKey);
      throw error;
    });

  inflightRequests.set(cacheKey, promise);
  return promise;
}

/**
 * Clear cache for a specific key or all cache
 */
function clearCache(key?: string) {
  if (key) {
    requestCache.delete(key);
  } else {
    requestCache.clear();
  }
}

class ApiClient {
  /**
   * Fetch locations (with caching)
   */
  async getLocations(): Promise<Location[] | null> {
    return getCachedOrFetch('locations', async () => {
      try {
        const response = await fetch(`${API_URL}/api/locations`);
        
        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.error('Non-JSON response from /api/locations:', response.status, text.substring(0, 100));
          return null;
        }
        
        const data = await response.json();
        return data.success ? data.locations : null;
      } catch (error) {
        console.error('Failed to load locations:', error);
        return null;
      }
    });
  }

  /**
   * Fetch templates (filtered by user's workspace, with caching)
   */
  async getTemplates(): Promise<Template[] | null> {
    const endpointKey = 'templates';
    const cooldown = isEndpointInCooldown(endpointKey);
    if (cooldown.active) {
      return null;
    }

    const token = getSecureToken('auth_token');
    const cacheKey = token ? `templates:${token.substring(0, 10)}` : 'templates:anonymous';
    
    return getCachedOrFetch(cacheKey, async () => {
      try {
        const headers: HeadersInit = {};
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetchWithAuth(
          `${API_URL}/api/templates`,
          { headers },
          { maxRetries: 0 }
        );
        if (response.status === 429) {
          setEndpointCooldown(endpointKey, response, 15000);
          return null;
        }
        
        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.error('Non-JSON response from /api/templates:', response.status, text.substring(0, 100));
          return null;
        }
        
        const data = await response.json();
        if (!data.success || !data.templates) return null;

        // Templates now come with full details from Directus, filtered by workspace
        return data.templates;
      } catch (error) {
        console.error('Failed to load templates:', error);
        return null;
      }
    });
  }

  /**
   * Fetch presets (with caching)
   */
  async getPresets(): Promise<PresetData | null> {
    return getCachedOrFetch('presets', async () => {
      try {
        const response = await fetch(`${API_URL}/api/presets`);
        
        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.error('Non-JSON response from /api/presets:', response.status, text.substring(0, 100));
          return null;
        }
        
        const data = await response.json();
        return data.success ? data.presets : null;
      } catch (error) {
        console.error('Failed to load presets:', error);
        return null;
      }
    });
  }

  /**
   * Generate image
   */
  async generateImage(
    requestBody: Record<string, any>,
    options?: { signal?: AbortSignal }
  ): Promise<GenerateImageResponse> {
    const token = getSecureToken('auth_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    // Add auth token if available (for user attribution in Directus)
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetchWithAuth(`${API_URL}/api/generate`, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
      signal: options?.signal
    });

    // Check if response is JSON before parsing
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      throw new Error(`Server returned ${response.status} ${response.statusText}. ${text.substring(0, 200)}`);
    }

    const data = await response.json();

    if (!response.ok || !data.success) {
      if (response.status === 429) {
        const retryAfter = data.retryAfter;
        let errorMsg = data.message || data.error || 'Rate limit exceeded';
        if (retryAfter) {
          errorMsg += ` Try again in ${retryAfter} seconds.`;
        }
        throw new ApiError(errorMsg, false, 429);
      }
      if (response.status === 503 && data.retryable) {
        throw new ApiError(data.message || data.error || 'Service temporarily unavailable', true, 503);
      }
      throw new ApiError(data.message || data.error || 'Error generating image', false, response.status);
    }

    return data;
  }

  async createTopupCheckout(workspaceId: number, topupPriceId: string, quantity = 1): Promise<BillingTopupCheckoutResponse> {
    const token = getSecureToken('auth_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetchWithAuth(`${BILLING_API_URL}/billing/checkout/topup`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        workspace_id: workspaceId,
        topup_price_id: topupPriceId,
        quantity
      })
    });

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      const text = await response.text();
      throw new Error(`Billing checkout failed (${response.status}): ${text.substring(0, 120)}`);
    }

    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.message || data?.error || 'Failed to start top-up checkout');
    }
    return data;
  }

  async createSubscriptionCheckout(workspaceId: number, priceId: string): Promise<BillingTopupCheckoutResponse> {
    const token = getSecureToken('auth_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetchWithAuth(`${BILLING_API_URL}/billing/checkout/subscription`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        workspace_id: workspaceId,
        price_id: priceId
      })
    });

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      const text = await response.text();
      throw new Error(`Billing checkout failed (${response.status}): ${text.substring(0, 120)}`);
    }

    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.message || data?.error || 'Failed to start subscription checkout');
    }
    return data;
  }

  async getBillingCatalog(currency: string): Promise<BillingCatalogResponse> {
    const normalizedCurrency = String(currency || '').trim().toLowerCase();
    const cacheKey = `billing-catalog:${normalizedCurrency || 'all'}`;
    return getCachedOrFetch(cacheKey, async () => {
      const query = normalizedCurrency ? `?currency=${encodeURIComponent(normalizedCurrency)}` : '';
      const response = await fetchWithAuth(`${BILLING_API_URL}/billing/public/plans${query}`, { method: 'GET' });
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Billing catalog failed (${response.status}): ${text.substring(0, 120)}`);
      }
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || data?.error || 'Failed to load billing catalog');
      }
      return {
        success: true,
        plans: Array.isArray(data.plans) ? data.plans : [],
        topups: Array.isArray(data.topups) ? data.topups : []
      };
    }, 60_000);
  }

  /**
   * Get list of generated images with optional pagination
   * @param options - { limit?: number, offset?: number } for pagination
   * @returns { success, images, total_count?, source }
   */
  async getImages(options?: { limit?: number; offset?: number; designSessionId?: string }): Promise<{
    success: boolean;
    images: ImageInfo[];
    total_count?: number;
    source?: string;
  }> {
    const endpointKey = 'images';
    const cooldown = isEndpointInCooldown(endpointKey);
    if (cooldown.active) {
      return { success: false, images: [], source: 'rate_limited' as const };
    }

    const token = getSecureToken('auth_token');
    const limit = options?.limit ?? 50;
    const offset = options?.offset ?? 0;
    const designSessionId = options?.designSessionId;
    const isPaginated = offset > 0 || limit !== 50;
    const cacheKey = isPaginated
      ? `images:${token?.substring(0, 10) || 'anon'}:${offset}:${limit}:${designSessionId || 'all'}`
      : token ? `images:${token.substring(0, 10)}:${designSessionId || 'all'}` : `images:anonymous:${designSessionId || 'all'}`;

    const fetcher = async () => {
      try {
        const headers: Record<string, string> = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        const params = new URLSearchParams();
        if (limit !== 50) params.set('limit', String(limit));
        if (offset > 0) params.set('offset', String(offset));
        if (designSessionId) params.set('designSessionId', designSessionId);
        const query = params.toString();
        const url = `${API_URL}/api/images${query ? '?' + query : ''}`;

        const response = await fetchWithAuth(
          url,
          { headers },
          { maxRetries: 0 }
        );
        if (response.status === 429) {
          setEndpointCooldown(endpointKey, response, 10000);
          return { success: false, images: [], source: 'rate_limited' as const };
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.error('Non-JSON response from /api/images:', response.status, text.substring(0, 100));
          return { success: false, images: [], source: 'error' as const };
        }

        const data = await response.json();
        return data.success
          ? { ...data, total_count: data.total_count ?? data.count }
          : { success: false, images: [], source: 'error' as const };
      } catch (error) {
        console.error('Failed to load images:', error);
        return { success: false, images: [], source: 'error' as const };
      }
    };

    if (isPaginated) {
      return fetcher();
    }
    return getCachedOrFetch(cacheKey, fetcher, 10000);
  }

  async getDesignSessionState(sessionId: string): Promise<{ success: boolean; state: DesignSessionState | null }> {
    const endpointKey = `design-session-state:${sessionId}`;
    const cooldown = isEndpointInCooldown(endpointKey);
    if (cooldown.active) {
      return { success: false, state: null };
    }

    try {
      const token = getSecureToken('auth_token');
      if (!token) {
        return { success: false, state: null };
      }

      const response = await fetchWithAuth(
        `${API_URL}/api/design-sessions/${encodeURIComponent(sessionId)}/state`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        },
        { maxRetries: 0 }
      );
      if (response.status === 429) {
        setEndpointCooldown(endpointKey, response, 10000);
        return { success: false, state: null };
      }
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return { success: false, state: null };
      }

      const data = await response.json();
      if (!response.ok || !data?.success || !data?.state) {
        return { success: false, state: null };
      }

      return {
        success: true,
        state: {
          geminiHistory: Array.isArray(data.state.geminiHistory) ? data.state.geminiHistory : [],
          thoughtSignature: typeof data.state.thoughtSignature === 'string' ? data.state.thoughtSignature : null,
          lastParams: data.state.lastParams && typeof data.state.lastParams === 'object' ? data.state.lastParams : null
        }
      };
    } catch (error) {
      console.error('Failed to load design session state:', error);
      return { success: false, state: null };
    }
  }

  /**
   * Rate an image (CSAT 1-5 stars)
   */
  async rateImage(imageId: string, rating: number): Promise<ApiResponse> {
    try {
      if (rating < 1 || rating > 5) {
        throw new Error('Rating must be between 1 and 5');
      }

      const token = getSecureToken('auth_token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetchWithAuth(`${API_URL}/api/images/${imageId}/rating`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ rating })
      });

      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(text || `Server returned ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to save rating');
      }
      return { success: true, message: data.message || 'Rating saved successfully' };
    } catch (error) {
      console.error('Failed to rate image:', error);
      if (error instanceof SyntaxError) {
        // JSON parsing error
        return { success: false, error: 'Invalid response from server' };
      }
      return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
  }

  /**
   * Update image tags
   */
  async updateImageTags(imageId: string, tags: string[]): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/api/images/${imageId}/tags`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ tags })
      });
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response from PATCH /api/images/tags:', response.status, text.substring(0, 100));
        return { success: false, error: `Server returned ${response.status} ${response.statusText}` };
      }
      
      const data = await response.json();
      
      // Clear images cache after successful update
      if (data.success) {
        const cacheKey = token ? `images:${token.substring(0, 10)}` : 'images:anonymous';
        clearCache(cacheKey);
      }
      
      return data;
    } catch (error) {
      console.error('Failed to update tags:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update tags' };
    }
  }

  /**
   * Delete an image by slug/ID
   * Returns success immediately, deletion happens asynchronously on server
   * This method is fire-and-forget - don't wait for it in UI
   */
  async deleteImage(imageId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: Record<string, string> = {};
      
      // Add auth token if available (optional - server uses admin token anyway)
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/api/images/${imageId}`, { 
        method: 'DELETE',
        headers 
      });
      
      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response from DELETE /api/images:', response.status, text.substring(0, 100));
        return { success: false, error: `Server returned ${response.status} ${response.statusText}` };
      }
      
      const data = await response.json();
      
      // Clear images cache after successful deletion
      if (data.success) {
        const cacheKey = token ? `images:${token.substring(0, 10)}` : 'images:anonymous';
        clearCache(cacheKey);
      }
      
      return data;
    } catch (error) {
      console.error('Failed to delete image:', error);
      if (error instanceof SyntaxError) {
        return { success: false, error: 'Invalid response from server' };
      }
      return { success: false, error: error instanceof Error ? error.message : 'Failed to delete image' };
    }
  }

  /**
   * Clear cache for specific key or all cache
   * Useful for invalidating cache after mutations (create/update/delete)
   */
  clearCache(key?: string) {
    clearCache(key);
  }
}

export default new ApiClient();
